import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Tag, Typography, Spin, Button, Tooltip, Modal, Table } from 'antd';
import {
  ExperimentOutlined, CheckCircleOutlined, ClockCircleOutlined,
  FileTextOutlined, ReloadOutlined, RightOutlined, QuestionCircleOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { partnerAPI } from '../services/api';
import ScheduleDetailModal from '../components/ScheduleDetailModal';
import { formatDateTime } from '../components/InquirySection';
import { useFillHeight } from '../hooks/useViewportFit';

const { Title, Text } = Typography;

// ── 상태 정의 ────────────────────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  '시작전':      '#94a3b8',
  '처리완료':    '#3b82f6',
  '조사완료':    '#f59e0b',
  '성적서작성중': '#8b5cf6',
  '성적서완료':  '#10b981',
  '발급완료':    '#0ea5e9',
  '최종종료':    '#64748b',
};
const STATUS_PIPELINE = ['시작전', '처리완료', '조사완료', '성적서작성중', '성적서완료', '발급완료', '최종종료'];
const COMPLETE_STATUSES = new Set(['성적서완료', '발급완료', '최종종료']);

interface SeasonStatus { season: string; total: number; breakdown: { status: string; count: number }[]; }
interface DashboardData {
  total_tests: number; in_progress: number; completed: number; report_ready: number;
  status_breakdown: { status: string; count: number }[];
  season_status: SeasonStatus[];
  category_breakdown: { category: string; count: number }[];
  recent_activities: any[];
}

// ── 도움말(?) ────────────────────────────────────────────
/** 카드별 집계 설명. 백엔드 `/api/partner/dashboard` 쿼리 기준과 1:1로 맞춘다. */
interface Hint { scope: string; rule: string; note?: string }

const hintBody = (h: Hint) => (
  <div style={{ fontSize: 11.5, lineHeight: 1.7, padding: '2px 0' }}>
    <div style={{ marginBottom: 5 }}>
      <span style={{ fontWeight: 700 }}>🎯 대상구간</span>
      <div style={{ opacity: 0.92 }}>{h.scope}</div>
    </div>
    <div style={{ marginBottom: h.note ? 5 : 0 }}>
      <span style={{ fontWeight: 700 }}>📊 집계기준</span>
      <div style={{ opacity: 0.92 }}>{h.rule}</div>
    </div>
    {h.note && (
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.18)', paddingTop: 4 }}>
        <span style={{ fontWeight: 700 }}>💡 참고</span>
        <div style={{ opacity: 0.92 }}>{h.note}</div>
      </div>
    )}
  </div>
);

/** 카드 제목/모서리에 붙는 물음표 아이콘. 클릭·호버 모두로 열린다(모바일 대응). */
const HelpHint: React.FC<{ hint: Hint; color?: string }> = ({ hint, color = '#d97706' }) => {
  const [hover, setHover] = useState(false);
  return (
    <Tooltip
      title={hintBody(hint)}
      trigger={['hover', 'click']}
      color="#1e293b"
      overlayStyle={{ maxWidth: 330 }}
      placement="topRight"
    >
      <QuestionCircleOutlined
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          fontSize: 12, color, cursor: 'help',
          opacity: hover ? 1 : 0.5,
          transition: 'opacity 0.15s',
        }}
      />
    </Tooltip>
  );
};

// ── KPI 카드 ─────────────────────────────────────────────
const KpiCard: React.FC<{
  icon: React.ReactNode; value: number; label: string; sub: string;
  color: string; bg: string; border: string; highlight?: boolean; hint?: Hint;
}> = ({ icon, value, label, sub, color, bg, border, highlight, hint }) => (
  <div style={{
    background: highlight ? `linear-gradient(135deg, ${bg}, #fff)` : bg,
    border: `1.5px solid ${border}`,
    borderRadius: 14, padding: '16px 18px',
    display: 'flex', alignItems: 'center', gap: 14,
    boxShadow: highlight ? `0 4px 16px ${color}18` : '0 1px 4px rgba(0,0,0,0.04)',
    position: 'relative', overflow: 'hidden',
  }}>
    <div style={{
      width: 44, height: 44, borderRadius: 11, flexShrink: 0,
      background: `${color}1a`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 20, color,
    }}>{icon}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 26, fontWeight: 900, color: '#1e293b', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color, marginTop: 2 }}>{label}</div>
      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{sub}</div>
    </div>
    {hint && (
      <div style={{ position: 'absolute', top: 7, right: 8, lineHeight: 1 }}>
        <HelpHint hint={hint} color={color} />
      </div>
    )}
  </div>
);

// ── 상태 파이프라인 ──────────────────────────────────────
const StatusPipeline: React.FC<{
  breakdown: { status: string; count: number }[]; total: number;
  onStatusClick?: (status: string) => void;
}> = ({ breakdown, total, onStatusClick }) => {
  const map: Record<string, number> = {};
  breakdown.forEach(b => { map[b.status] = b.count; });
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {STATUS_PIPELINE.map(status => {
        const count = map[status] || 0;
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        const color = STATUS_COLORS[status] || '#94a3b8';
        const isDone = COMPLETE_STATUSES.has(status);
        const clickable = count > 0 && !!onStatusClick;

        return (
          <div
            key={status}
            title={clickable ? `${status} 시험 목록 보기` : undefined}
            onClick={clickable ? () => onStatusClick(status) : undefined}
            onMouseEnter={() => clickable && setHovered(status)}
            onMouseLeave={() => setHovered(h => (h === status ? null : h))}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              cursor: clickable ? 'pointer' : 'default',
              background: hovered === status ? `${color}0d` : 'transparent',
              borderRadius: 6, padding: '1px 4px', margin: '-1px -4px',
              transition: 'background 0.15s',
            }}
          >
            {/* 상태명 */}
            <div style={{ width: 86, flexShrink: 0, textAlign: 'right' }}>
              <span style={{
                fontSize: 11, fontWeight: count > 0 ? 700 : 400,
                color: count > 0 ? color : '#d1d5db',
              }}>{status}</span>
            </div>
            {/* 막대 */}
            <div style={{ flex: 1, height: 18, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
              {count > 0 && (
                <div style={{
                  width: `${Math.max(pct, 2)}%`, height: '100%',
                  background: isDone
                    ? `linear-gradient(90deg, ${color}cc, ${color})`
                    : color,
                  borderRadius: 4,
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                  paddingRight: pct > 8 ? 6 : 0,
                  transition: 'width 0.6s ease',
                }}>
                  {pct > 8 && (
                    <span style={{ fontSize: 10, color: '#fff', fontWeight: 700 }}>{count}건</span>
                  )}
                </div>
              )}
            </div>
            {/* 건수 + 비율 */}
            <div style={{ width: 64, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: count > 0 ? color : '#d1d5db', minWidth: 28, textAlign: 'right' }}>
                {count}건
              </span>
              {count > 0 && (
                <span style={{ fontSize: 10, color: '#94a3b8' }}>{pct}%</span>
              )}
            </div>
          </div>
        );
      })}
      {/* 플로우 화살표 힌트 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, paddingLeft: 96 }}>
        {['등록', '처리', '조사', '보고서 작성', '완료', '발급', '종료'].map((step, i, arr) => (
          <React.Fragment key={step}>
            <span style={{ fontSize: 9, color: '#cbd5e1', whiteSpace: 'nowrap' }}>{step}</span>
            {i < arr.length - 1 && <RightOutlined style={{ fontSize: 8, color: '#e2e8f0' }} />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// ── 시즌 미니카드 ────────────────────────────────────────
const SeasonMini: React.FC<{ season?: SeasonStatus; label: string; isSummer?: boolean; isPrev?: boolean; hint?: Hint }> = ({ season, label, isSummer, isPrev, hint }) => {
  const total = season?.total ?? 0;
  const completedCnt = (season?.breakdown || []).filter(b => COMPLETE_STATUSES.has(b.status)).reduce((s, b) => s + b.count, 0);
  const pct = total > 0 ? Math.round((completedCnt / total) * 100) : 0;
  const color = isSummer ? '#f59e0b' : '#3b82f6';
  const bg    = isSummer ? '#fffbeb' : '#eff6ff';
  const border = isSummer ? '#fde68a' : '#bfdbfe';

  return (
    <div style={{
      flex: 1, background: bg, border: `1px solid ${border}`, borderRadius: 12,
      padding: '12px 14px', textAlign: 'center',
      position: 'relative',
    }}>
      {/* ? 는 흐름에서 빼 모서리에 고정 — 인라인이면 좁은 카드에서 제목이 줄바꿈됨 */}
      {hint && (
        <div style={{ position: 'absolute', top: 6, right: 7, lineHeight: 1 }}>
          <HelpHint hint={hint} color={color} />
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color, whiteSpace: 'nowrap' }}>{isSummer ? '☀️' : '❄️'} {label}</span>
        {isPrev && <span style={{ fontSize: 9, color: '#94a3b8', background: '#f1f5f9', padding: '0 4px', borderRadius: 6, whiteSpace: 'nowrap' }}>전년</span>}
      </div>
      <div style={{ fontSize: 24, fontWeight: 900, color: total > 0 ? '#1e293b' : '#d1d5db', lineHeight: 1.1 }}>{total}</div>
      <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 6 }}>건</div>
      {total > 0 ? (
        <>
          <div style={{ height: 4, background: '#e2e8f0', borderRadius: 2, overflow: 'hidden', marginBottom: 4 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2 }} />
          </div>
          <div style={{ fontSize: 10, color, fontWeight: 600 }}>완료율 {pct}%</div>
        </>
      ) : (
        <div style={{ fontSize: 10, color: '#d1d5db' }}>해당 없음</div>
      )}
    </div>
  );
};

// ── 구분별 바 ────────────────────────────────────────────
// 표기·색상은 관리자(lab) 대시보드 '구분별 시험현황' 도넛과 동일 규칙
const CATEGORY_DISPLAY_NAME: Record<string, string> = { '살균': '살균제', '살충': '살충제', '제초': '제초제' };
const categoryDisplayName = (name: string) => CATEGORY_DISPLAY_NAME[name] || name;
const CATEGORY_COLORS: Record<string, string> = {
  '살균':   '#f472b6', '살균제': '#f472b6',
  '살충':   '#10b981', '살충제': '#10b981',
  '제초제': '#facc15', '제초':   '#facc15',
  '기초시험': '#f59e0b',
};
/* 관리자 대시보드 도넛과 동일한 표시 순서 — 살균제 → 살충제 → 제초제 → 기초시험 → 기타 */
const CATEGORY_LEGEND_ORDER = ['살균제', '살균', '살충제', '살충', '제초제', '제초', '기초시험'];
const categoryRank = (name: string) => {
  const i = CATEGORY_LEGEND_ORDER.indexOf(name);
  return i < 0 ? CATEGORY_LEGEND_ORDER.length : i;
};

const CategoryBar: React.FC<{ breakdown: { category: string; count: number }[] }> = ({ breakdown }) => {
  const total = breakdown.reduce((s, b) => s + b.count, 0);
  const COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#f97316', '#14b8a6', '#e879f9', '#06b6d4'];
  const sorted = [...breakdown].sort((a, b) => categoryRank(a.category) - categoryRank(b.category));

  if (!breakdown.length) return <div style={{ fontSize: 12, color: '#d1d5db', textAlign: 'center', padding: '8px 0' }}>데이터 없음</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {sorted.map((b, i) => {
        const pct = total > 0 ? Math.round((b.count / total) * 100) : 0;
        const color = CATEGORY_COLORS[b.category] || COLORS[i % COLORS.length];
        return (
          <div key={b.category} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 52, flexShrink: 0, textAlign: 'right', fontSize: 11, color: '#475569', fontWeight: 600 }}>{categoryDisplayName(b.category)}</div>
            <div style={{ flex: 1, height: 14, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${Math.max(pct, 2)}%`, height: '100%', background: color, borderRadius: 3 }} />
            </div>
            <div style={{ width: 52, flexShrink: 0, fontSize: 11, color: '#64748b' }}>
              <span style={{ fontWeight: 700, color }}>{b.count}건</span>
              <span style={{ color: '#94a3b8', marginLeft: 3 }}>{pct}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── 최근 활동 리스트 ─────────────────────────────────────
const RecentList: React.FC<{ activities: any[]; onSelect: (item: any) => void }> = ({ activities, onSelect }) => {
  if (!activities.length) return (
    <div style={{ textAlign: 'center', padding: '20px 0', color: '#d1d5db', fontSize: 12 }}>최근 활동 없음</div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {activities.slice(0, 8).map((item, idx) => (
        <div key={item.id}
          onClick={() => onSelect(item)}
          onMouseEnter={e => { e.currentTarget.style.background = '#fffbeb'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 6px', margin: '0 -6px',
            cursor: 'pointer', borderRadius: 6, transition: 'background 0.15s',
            borderBottom: idx < Math.min(activities.length, 8) - 1 ? '1px solid #f8fafc' : 'none',
          }}>
          <Tag
            style={{
              fontSize: 10, padding: '1px 6px', margin: 0, flexShrink: 0,
              background: `${STATUS_COLORS[item.status] || '#94a3b8'}18`,
              border: `1px solid ${STATUS_COLORS[item.status] || '#94a3b8'}44`,
              color: STATUS_COLORS[item.status] || '#64748b',
              borderRadius: 8,
            }}
          >
            {item.status}
          </Tag>
          <Tooltip title={item.title}>
            <span style={{
              fontSize: 12, flex: 1, minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              color: '#334155', fontWeight: 500,
            }}>
              {item.title}
            </span>
          </Tooltip>
          <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>
            {item.location_name || '—'}
          </span>
          <span style={{ fontSize: 10, color: '#b0bac6', flexShrink: 0, minWidth: 50, textAlign: 'right' }}>
            {item.start_date || '—'}
          </span>
        </div>
      ))}
    </div>
  );
};

// ── 메인 대시보드 ────────────────────────────────────────
const DashboardPage: React.FC = () => {
  /* 페이지를 뷰포트 바닥까지 실측한 높이로 고정 — 문서 세로스크롤 방지.
     마지막 행(시즌·구분·최근활동)이 남는 공간을 받고 넘치면 그 안에서만 스크롤한다. */
  const { ref: fillRef, height: fillHeight } = useFillHeight(480);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  // 상태 클릭 → 해당 상태 시험 목록 모달
  const [statusModal, setStatusModal] = useState<string | null>(null);
  const [statusList, setStatusList] = useState<any[]>([]);
  const [statusListLoading, setStatusListLoading] = useState(false);

  // 목록 행 클릭 → 시험일정 화면과 동일한 상세 모달
  const [detailRecord, setDetailRecord] = useState<any | null>(null);

  // 로그인 직후 첫 화면 — 담당자 답변 도착 알림
  const navigate = useNavigate();
  const [replyAlerts, setReplyAlerts] = useState<any[]>([]);
  const [replyAlertOpen, setReplyAlertOpen] = useState(false);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    partnerAPI.inquirySummary()
      .then(({ data }) => {
        const items = data?.items || [];
        setReplyAlerts(items);
        if (items.length > 0) setReplyAlertOpen(true);
      })
      .catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try { const res = await partnerAPI.dashboard(); setData(res.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  };

  const openStatusModal = async (status: string) => {
    setStatusModal(status);
    setStatusList([]);
    setStatusListLoading(true);
    try {
      // 대시보드 통계와 동일하게 당해연도 기준으로 조회
      const yy = String(new Date().getFullYear()).slice(2);
      const res = await partnerAPI.schedules({ status, test_year: yy });
      setStatusList(res.data);
    } catch {
      setStatusList([]);
    } finally {
      setStatusListLoading(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <Spin size="large" />
    </div>
  );

  if (!data) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <Text type="secondary">데이터를 불러오지 못했습니다.</Text>
      <br />
      <Button onClick={load} style={{ marginTop: 12 }} icon={<ReloadOutlined />}>다시 시도</Button>
    </div>
  );

  const thisYear = new Date().getFullYear();
  const yy      = String(thisYear).slice(2);
  const prevYY  = String(thisYear - 1).slice(2);
  const nextYY  = String(thisYear + 1).slice(2);

  const prevDong = data.season_status.find(s => s.season === `${prevYY}동`);
  const thisHa   = data.season_status.find(s => s.season === `${yy}하`);
  const thisDong = data.season_status.find(s => s.season === `${yy}동`);

  // 완료 여부 판단용
  const reportCount = data.status_breakdown
    .filter(b => b.status === '성적서완료' || b.status === '발급완료')
    .reduce((s, b) => s + b.count, 0);

  /* 모든 카드 공통 — 조회 가능한 시험의 범위 */
  const SCOPE_COMMON = '귀사(계열 의뢰사 포함)로 연결된 시험 + 개별 열람 허용된 시험';

  const kpiItems = [
    {
      icon: <ExperimentOutlined />,
      value: data.total_tests,
      label: `${yy}년 전체`,
      sub: '등록된 시험 건수',
      color: '#f59e0b', bg: '#fffbeb', border: '#fde68a',
      highlight: true,
      hint: {
        scope: `시험년도 ${yy}년(20${yy}) 등록 시험 전체`,
        rule: '진행상태와 무관하게 등록된 모든 건을 집계합니다.',
        note: `${SCOPE_COMMON}. 아래 3개 카드도 모두 이 범위 안에서 계산됩니다.`,
      } as Hint,
    },
    {
      icon: <ClockCircleOutlined />,
      value: data.in_progress,
      label: '진행중',
      sub: '처리·조사·보고서 단계',
      color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe',
      hint: {
        scope: `${yy}년 시험 중 시험이 착수되어 진행 단계에 있는 건`,
        rule: '진행상태가 처리완료 · 조사완료 · 성적서작성중 인 건의 합계입니다.',
        note: '아직 착수 전인 「시작전」은 포함되지 않아, 전체 = 진행중 + 완료 가 아닐 수 있습니다.',
      } as Hint,
    },
    {
      icon: <CheckCircleOutlined />,
      value: data.completed,
      label: '완료',
      sub: '성적서완료·발급·종료',
      color: '#10b981', bg: '#f0fdf4', border: '#bbf7d0',
      hint: {
        scope: `${yy}년 시험 중 성적서 단계까지 마무리된 건`,
        rule: '진행상태가 성적서완료 · 발급완료 · 최종종료 인 건의 합계입니다.',
        note: '「시험 시즌 현황」의 완료율도 이 3개 상태를 기준으로 계산합니다.',
      } as Hint,
    },
    {
      icon: <FileTextOutlined />,
      value: reportCount,
      label: '성적서 발급',
      sub: '성적서완료 + 발급완료',
      color: '#8b5cf6', bg: '#faf5ff', border: '#e9d5ff',
      hint: {
        scope: `${yy}년 시험 중 성적서가 산출된 건`,
        rule: '진행상태가 성적서완료 · 발급완료 인 건의 합계입니다.',
        note: '「최종종료」는 제외하므로 왼쪽 「완료」 카드보다 작을 수 있습니다.',
      } as Hint,
    },
  ];

  /* 시즌 카드 — 동작물은 시작연도~수확연도 2개년에 걸친 시즌 */
  const dongHint = (startYY: string, endYY: string, prev: boolean): Hint => ({
    scope: `20${startYY}년 가을 시작 ~ 20${endYY}년 마무리 동작물 시험${prev ? ' (전년도 시즌)' : ''}`,
    rule: `시험년도 ${startYY}년 + 동절기로 등록된 건, 그리고 시험년도가 ${endYY}년이더라도 시험번호가 SR${startYY}로 시작하는 건(시작연도 기준)을 함께 집계합니다.`,
    note: `동작물은 가을에 시작해 이듬해 마무리되어 시작연도~수확연도로 표기합니다. 시험년도가 ${yy}년이 아닌 건도 포함되므로 상단 ${yy}년 KPI에는 잡히지 않을 수 있습니다.`,
  });
  const haHint: Hint = {
    scope: `20${yy}년 하작물 시험`,
    rule: `시험년도 ${yy}년 + 하절기로 등록된 건을 집계합니다.`,
    note: '하작물은 봄~가을에 개시·완결되는 당해년도 시험이라 단일 연도로 집계됩니다.',
  };

  return (
    /* 실측 높이 고정 + 내부 폴백 스크롤 — 최소높이 미달 화면에서도 문서 스크롤은 생기지 않는다 */
    <div ref={fillRef} style={{ display: 'flex', flexDirection: 'column', gap: 10, height: fillHeight, overflowY: 'auto', overflowX: 'hidden' }}>

      {/* 헤더 */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#92400e' }}>대시보드</Title>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Text type="secondary">
              시험년도 {yy}년 기준 현황 · 시즌 집계만 {prevYY}~{yy} 동작물 · {yy} 하작물 · {yy}~{nextYY} 동작물
            </Text>
            <HelpHint hint={{
              scope: `시험년도가 ${yy}년(20${yy})으로 등록된 시험`,
              rule: '상단 KPI 4종과 진행상태 분포 · 시험 구분별 · 최근 시험 현황은 모두 이 기준으로 집계합니다.',
              note: `「시험 시즌 현황」만 작물 재배시기 기준입니다. 동작물은 2개년에 걸쳐 ${prevYY}년·${nextYY}년으로 등록된 건까지 포함하므로 시즌 합계가 위 숫자와 다를 수 있습니다.`,
            }} />
          </span>
        </div>
        <Button size="small" icon={<ReloadOutlined />} onClick={load} style={{ color: '#d97706' }}>
          새로고침
        </Button>
      </div>

      {/* KPI */}
      <Row gutter={[10, 10]}>
        {kpiItems.map(item => (
          <Col xs={12} sm={6} key={item.label}>
            <KpiCard {...item} />
          </Col>
        ))}
      </Row>

      {/* 상태 파이프라인 */}
      <Card
        size="small"
        style={{ borderRadius: 14, border: '1px solid #fef3c7', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>진행상태 분포</span>
              <HelpHint hint={{
                scope: `${yy}년 등록 시험 전체 (총 ${data.total_tests}건)`,
                rule: '시험 진행 7단계별 건수와 전체 대비 비율(%)입니다. 막대를 클릭하면 해당 상태의 시험 목록이 열립니다.',
                note: '비율은 소수점 반올림이라 합계가 100%와 1% 정도 차이 날 수 있습니다.',
              }} />
            </span>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>총 {data.total_tests}건 기준</span>
          </div>
        }
      >
        <StatusPipeline breakdown={data.status_breakdown} total={data.total_tests} onStatusClick={openStatusModal} />
      </Card>

      {/* 시즌 + 구분 + 최근 활동 — 남는 공간을 모두 받고, 넘치면 이 안에서만 스크롤.
           overflowX hidden: Row 의 음수 gutter 마진이 가로 스크롤바를 만들지 않게 */}
      <div style={{ flex: '1 1 auto', minHeight: 240, overflowY: 'auto', overflowX: 'hidden' }}>
      <Row gutter={[12, 12]}>

        {/* 좌: 시즌 + 구분 */}
        <Col xs={24} md={10}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* 시즌별 현황 */}
            <Card
              size="small"
              title={
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>시험 시즌 현황</span>
                  <HelpHint hint={{
                    scope: `${prevYY}~${yy} 동작물 · ${yy} 하작물 · ${yy}~${nextYY} 동작물 3개 시즌`,
                    rule: '시즌별 총 건수와 완료율입니다. 완료율 = (성적서완료 + 발급완료 + 최종종료) ÷ 시즌 전체.',
                    note: '☀️ 하작물은 봄~가을에 개시·완결되는 당해년도 시험, ❄️ 동작물은 가을에 시작해 이듬해 마무리되는 시험입니다. 시험일정 목록의 작기 퀵필터와 동일한 규칙이며, 각 시즌의 ? 에서 개별 기준을 확인할 수 있습니다.',
                  }} />
                </span>
              }
              style={{ borderRadius: 14, border: '1px solid #fef3c7' }}
            >
              <div style={{ display: 'flex', gap: 8 }}>
                <SeasonMini season={prevDong} label={`${prevYY}~${yy} 동작물`} isPrev hint={dongHint(prevYY, yy, true)} />
                <SeasonMini season={thisHa}   label={`${yy} 하작물`} isSummer hint={haHint} />
                <SeasonMini season={thisDong} label={`${yy}~${nextYY} 동작물`} hint={dongHint(yy, nextYY, false)} />
              </div>
            </Card>

            {/* 구분별 현황 */}
            {data.category_breakdown.length > 0 && (
              <Card
                size="small"
                title={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>시험 구분별</span>
                    <HelpHint hint={{
                      scope: `${yy}년 등록 시험 중 시험 구분이 지정된 건`,
                      rule: '살균제 · 살충제 · 제초제 등 약제 구분별 건수와 비율입니다. 건수가 많은 구분부터 표시합니다.',
                      note: '구분이 아직 지정되지 않은 시험은 제외되므로 합계가 위쪽 「전체」보다 적을 수 있습니다.',
                    }} />
                  </span>
                }
                style={{ borderRadius: 14, border: '1px solid #fef3c7' }}
              >
                <CategoryBar breakdown={data.category_breakdown} />
              </Card>
            )}
          </div>
        </Col>

        {/* 우: 최근 시험 현황 */}
        <Col xs={24} md={14}>
          <Card
            size="small"
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>최근 시험 현황</span>
                  <HelpHint hint={{
                    scope: `${yy}년 등록 시험 중 최근 변경된 상위 8건`,
                    rule: '시험 정보가 마지막으로 수정된 시각(진행상태 변경 포함) 기준 최신순입니다.',
                    note: '오른쪽 날짜는 수정일이 아닌 시험 개시일입니다. 항목을 클릭하면 상세 정보가 표시되며, 전체 목록은 좌측 「시험일정」 메뉴에서 확인하세요.',
                  }} />
                </span>
                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>최근 업데이트순</span>
              </div>
            }
            style={{ borderRadius: 14, border: '1px solid #fef3c7', height: '100%' }}
          >
            <RecentList activities={data.recent_activities} onSelect={setDetailRecord} />
          </Card>
        </Col>

      </Row>
      </div>

      {/* 상태별 시험 목록 모달 */}
      <Modal
        open={!!statusModal}
        onCancel={() => setStatusModal(null)}
        footer={<Button onClick={() => setStatusModal(null)}>닫기</Button>}
        width={720}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
              background: STATUS_COLORS[statusModal || ''] || '#94a3b8',
            }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
              '{statusModal}' 시험 목록
            </span>
            {!statusListLoading && (
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>
                {String(new Date().getFullYear()).slice(2)}년 · 총 {statusList.length}건
              </span>
            )}
          </div>
        }
        styles={{ body: { maxHeight: '65vh', overflowY: 'auto' } }}
      >
        <Table
          size="small"
          rowKey="id"
          loading={statusListLoading}
          dataSource={statusList}
          pagination={statusList.length > 10 ? { pageSize: 10, size: 'small' } : false}
          onRow={record => ({
            onClick: () => setDetailRecord(record),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: '연번', key: 'no', width: 48, align: 'center' as const,
              render: (_: any, __: any, i: number) => i + 1 },
            { title: '시험번호', dataIndex: 'test_number', width: 130,
              render: (v: string) => <span style={{ fontWeight: 600, color: '#92400e', fontSize: 12 }}>{v}</span> },
            { title: '항목명', dataIndex: 'title', ellipsis: true },
            { title: '구분', dataIndex: 'category_name', width: 70, align: 'center' as const },
            { title: '시험장소', dataIndex: 'location_name', width: 90, align: 'center' as const },
            { title: '개시일', dataIndex: 'start_date', width: 84, align: 'center' as const },
          ]}
          locale={{ emptyText: '해당 상태의 시험이 없습니다.' }}
        />
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, textAlign: 'right' }}>
          행을 클릭하면 상세 정보가 표시됩니다
        </div>
      </Modal>

      {/* 문의 답변 도착 알림 (로그인 첫 화면) */}
      <Modal
        open={replyAlertOpen}
        onCancel={() => setReplyAlertOpen(false)}
        width={560}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <MessageOutlined style={{ color: '#f59e0b', fontSize: 18 }} />
            <span style={{ fontWeight: 700, fontSize: 15, color: '#92400e' }}>문의 답변 도착</span>
            <Tag color="orange">{replyAlerts.length}건</Tag>
          </span>
        }
        footer={[
          <Button key="close" onClick={() => setReplyAlertOpen(false)}>나중에 보기</Button>,
          <Button key="go" type="primary" onClick={() => { setReplyAlertOpen(false); navigate('/inquiries'); }}>
            문의/요청 바로가기
          </Button>,
        ]}
      >
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
          등록하신 문의/요청에 담당자 답변이 등록되었습니다.
        </div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {replyAlerts.map((q: any) => (
            <div
              key={q.id}
              onClick={() => { setReplyAlertOpen(false); navigate('/inquiries'); }}
              style={{
                background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10,
                padding: '10px 12px', marginBottom: 8, cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                <Tag color={q.inquiry_type === '요청' ? 'purple' : 'blue'} style={{ margin: 0 }}>{q.inquiry_type}</Tag>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#92400e' }}>{q.title}</span>
                {q.test_number && <Tag style={{ margin: 0 }}>{q.test_number}</Tag>}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                최근 답변 {formatDateTime(q.last_reply_at || q.created_at)} · 답글 {q.reply_count}건
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* 시험일정 화면과 동일한 상세 모달 (상태 목록 위에 표시) */}
      <ScheduleDetailModal
        open={!!detailRecord}
        scheduleId={detailRecord?.id ?? null}
        titleRecord={detailRecord}
        statusColorMap={STATUS_COLORS}
        onClose={() => setDetailRecord(null)}
        onChanged={load}
      />
    </div>
  );
};

export default DashboardPage;
